import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Row, Col, Pagination } from "react-bootstrap";
import { getAllStories } from "../../../../api/summaryReport";

const CustomPagination = ({ currentPage, totalPages, onPageChange }) => {
  const maxVisible = 3;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  const pages = [];
  for (let i = startPage; i <= endPage; i++) pages.push(i);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
      <Pagination style={{ margin: 0 }}>
        <Pagination.First onClick={() => onPageChange(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
        {startPage > 1 && (
          <>
            <Pagination.Item onClick={() => onPageChange(1)}>1</Pagination.Item>
            {startPage > 2 && <Pagination.Ellipsis disabled />}
          </>
        )}
        {pages.map((page) => (
          <Pagination.Item key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
            {page}
          </Pagination.Item>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
            <Pagination.Item onClick={() => onPageChange(totalPages)}>{totalPages}</Pagination.Item>
          </>
        )}
        <Pagination.Next onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    </div>
  );
};

const TaskPlanning = () => {
  const token = localStorage.getItem("token");
  const [rows, setRows] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  const totalPages = Math.ceil(rows.length / rowsPerPage);
  const paginatedRows = rows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  
  const fetchStories = useCallback(async () => {
    try {
      const response = await getAllStories(token);
      const stories = response.data;

      const today = new Date();
      const userMap = {};

      stories.forEach((story) => {

        if (!story.sprint) return;

        const start = new Date(story.sprint.startDate);
        const end = new Date(story.sprint.endDate);
        end.setHours(23, 59, 59, 999);

        //  Active Sprint Condition
        if (!(today >= start && today <= end)) return;

        const storyPoints = story.storyPoints || 0;
        const users = story.storyUsers || [];

        if (users.length === 0) return;

        //  remove duplicate users
        const uniqueUsers = [
          ...new Map(users.map((u) => [u.user.id, u])).values(),
        ];

        const splitPoints = storyPoints / uniqueUsers.length;

        uniqueUsers.forEach((su) => {
          const name = su.user.name;

          if (!userMap[name]) {
            userMap[name] = {
              name,
              assigned: 0,
              splited: 0,
              inProgress: 0,
              completed: 0,
            };
          }

          //  assigned points
          userMap[name].assigned += Number(splitPoints.toFixed(2));

          //  Splitted = only when multiple users
          if (users.length > 1) {
            userMap[name].splited += Number(splitPoints.toFixed(2));
          }

          // InProgress stories count
          if (story.status === "INPROGRESS") {
            userMap[name].inProgress += 1;
          }

          // Completed stories count
          if (story.status === "COMPLETED") {
            userMap[name].completed += 1;
          }
        });
      });

      setRows(Object.values(userMap));

    } catch (error) {
      console.error("Error fetching stories:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);


  return (
    <Row>
      <Col md={12}>
        <Card className="shadow-sm">
          <Card.Header
            className="text-center fw-semibold"
            style={{
              backgroundColor: "#f3f4f6",
              borderBottom: "1px solid #dee2e6",
            }}
          >
            Sprint Task Planning
          </Card.Header>

          <Card className="shadow-sm border-0">
            <Card.Body className="p-0">
              <style>
                {`
               
                .sprint-header th {
                  background-color: #cfe7df;
                  color: #2f3e3b;
                  font-weight: 600;
                  text-align: center;
                  border-color: #b7d6cc;
              }
                
              `}
              </style>
              <Table bordered hover responsive className="mb-0">
                <thead className="bg-success bg-opacity-25 text-center sprint-header">
                  <tr>
                    <th>Assignee</th>
                    <th>Assigned (S.P)</th>
                    <th>Splited (S.P)</th>
                    <th>InProgress (Stories)</th>
                    <th>Completed (Stories)</th>
                  </tr>
                </thead>

                <tbody className="text-center">
                  {paginatedRows.map((row, index) => (
                    <tr key={index}>
                      <td className="text-start fw-medium">{row.name}</td>
                      <td>{row.assigned}</td>
                      <td>{row.splited}</td>
                      <td>{row.inProgress}</td>
                      <td>{row.completed}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="px-3 py-3 d-flex justify-content-between align-items-center">
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                  <div style={{ fontSize: "13px", color: "#6c757d" }}>
                    Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                    {Math.min(currentPage * rowsPerPage, rows.length)} of{" "}
                    {rows.length} entries
                  </div>
                </div>
              )}

            </Card.Body>
          </Card>
        </Card>
      </Col>
    </Row>
  );
};

export default TaskPlanning;